import { addDoc, collection, getDocs, limit, orderBy, query, Timestamp, where, updateDoc, doc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { db } from '../firebase/config';
import { firestoreRestApi } from '../utils/firestoreRestApi';
import { normalizeMinutesForStorage } from '../utils/prTimeFormat';
import { RelayEventType, RelayResult } from '../types';

type RelayDateRange = { start: string; end: string };

const RELAY_COLLECTION = 'relayResults';

function safeRelayLegs(
  legTimesMinutes: RelayResult['legTimesMinutes'],
  legAthleteIds: RelayResult['legAthleteIds']
): { legTimesMinutes: [number, number, number, number]; legAthleteIds: [string, string, string, string]; legAthleteIdsKey: string } {
  const times = legTimesMinutes as unknown as [number, number, number, number];
  const ids = legAthleteIds as unknown as [string, string, string, string];
  const legAthleteIdsKey = ids.join('|');
  return { legTimesMinutes: times, legAthleteIds: ids, legAthleteIdsKey };
}

function normalizeRelayEventType(eventType: string): eventType is RelayEventType {
  return eventType === '4x800' || eventType === '4x100' || eventType === '4x400' || eventType === 'DMR';
}

function mapRelayDoc(docAny: any): RelayResult {
  const data = (docAny?.data ? docAny.data() : docAny) ?? docAny;

  // REST queryDocuments already includes fields at top-level + id.
  const id = data.id ?? docAny.id ?? '';
  const legTimes = data.legTimesMinutes ?? data.legTimes ?? [];
  const legIds = data.legAthleteIds ?? [];

  const legTimesTuple = legTimes.length === 4 ? (legTimes as [number, number, number, number]) : ([0, 0, 0, 0] as any);
  const legIdsTuple = legIds.length === 4 ? (legIds as [string, string, string, string]) : (['', '', '', ''] as any);

  return {
    id,
    teamId: data.teamId,
    eventType: data.eventType,
    date: data.date,
    location: data.location,
    overallTimeMinutes: data.overallTimeMinutes,
    legTimesMinutes: legTimesTuple,
    legAthleteIds: legIdsTuple,
    legAthleteIdsKey: data.legAthleteIdsKey ?? (legIdsTuple as any).join('|'),
    isVerified: data.isVerified ?? false,
    createdAt: data.createdAt ?? Timestamp.now(),
    updatedAt: data.updatedAt ?? Timestamp.now(),
  } as RelayResult;
}

async function runRelayQueryRest(
  teamId: string,
  eventType: string,
  range?: RelayDateRange,
  maxDocs: number = 5000
): Promise<RelayResult[]> {
  const filters: Array<{ field: string; op: string; value: string | number | boolean }> = [
    { field: 'teamId', op: '==', value: teamId },
    { field: 'eventType', op: '==', value: eventType },
  ];
  if (range) {
    filters.push({ field: 'date', op: 'GREATER_THAN_OR_EQUAL', value: range.start });
    filters.push({ field: 'date', op: 'LESS_THAN_OR_EQUAL', value: range.end });
  }

  const documents = await firestoreRestApi.queryDocuments(RELAY_COLLECTION, filters, maxDocs);
  return documents.map((d) => ({
    ...(d?.data ? d.data() : d),
    id: d.id,
  })) as unknown as RelayResult[];
}

export const relayService = {
  async upsertRelayResult(params: {
    teamId: string;
    eventType: RelayEventType;
    date: string; // yyyy-MM-dd
    location?: string;
    overallTimeMinutes: number;
    legTimesMinutes: RelayResult['legTimesMinutes'];
    legAthleteIds: RelayResult['legAthleteIds'];
    isVerified: boolean;
  }): Promise<{ id: string; created: boolean }> {
    const { teamId, eventType, date, location, overallTimeMinutes, legTimesMinutes, legAthleteIds, isVerified } = params;
    if (!normalizeRelayEventType(eventType)) {
      throw new Error(`Invalid relay eventType: ${eventType}`);
    }

    const { legTimesMinutes: timesTuple, legAthleteIds: idsTuple, legAthleteIdsKey } = safeRelayLegs(legTimesMinutes, legAthleteIds);
    const legsNorm = timesTuple.map(normalizeMinutesForStorage) as [number, number, number, number];

    const payload: Omit<RelayResult, 'id'> = {
      teamId,
      eventType,
      date,
      location: (location || '').trim() || undefined,
      overallTimeMinutes: normalizeMinutesForStorage(overallTimeMinutes),
      legTimesMinutes: legsNorm,
      legAthleteIds: idsTuple,
      legAthleteIdsKey,
      isVerified,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    } as any;

    try {
      if (Capacitor.getPlatform() === 'ios') {
        const existingDocs = await firestoreRestApi.queryDocuments(RELAY_COLLECTION, [
          { field: 'teamId', op: '==', value: teamId },
          { field: 'eventType', op: '==', value: eventType },
          { field: 'date', op: '==', value: date },
          { field: 'legAthleteIdsKey', op: '==', value: legAthleteIdsKey },
        ], 1);

        if (existingDocs.length > 0) {
          const existing = existingDocs[0];
          await firestoreRestApi.setDocument(RELAY_COLLECTION, existing.id, {
            ...payload,
            updatedAt: Timestamp.now(),
          }, true);
          return { id: existing.id, created: false };
        }

        const newId = await firestoreRestApi.createDocument(RELAY_COLLECTION, payload as any);
        return { id: newId, created: true };
      }

      const q = query(
        collection(db, RELAY_COLLECTION),
        where('teamId', '==', teamId),
        where('eventType', '==', eventType),
        where('date', '==', date),
        where('legAthleteIdsKey', '==', legAthleteIdsKey),
        limit(1)
      );

      const snapshot = await getDocs(q);
      if (snapshot.docs.length > 0) {
        const existing = snapshot.docs[0];
        await updateDoc(doc(db, RELAY_COLLECTION, existing.id), {
          ...payload,
          updatedAt: Timestamp.now(),
        });
        return { id: existing.id, created: false };
      }

      const docRef = await addDoc(collection(db, RELAY_COLLECTION), payload as any);
      return { id: docRef.id, created: true };
    } catch (e: any) {
      // Fall back to REST create if SDK fails due to permission/rules evaluation.
      const documents = await runRelayQueryRest(teamId, eventType, { start: date, end: date }, 10);
      void documents;
      const newId = await firestoreRestApi.createDocument(RELAY_COLLECTION, payload as any);
      return { id: newId, created: true };
    }
  },

  async getTeamRelayResults(
    teamId: string,
    eventType: RelayEventType,
    dateRange?: { start: string; end: string },
    maxDocs: number = 5000
  ): Promise<RelayResult[]> {
    if (Capacitor.getPlatform() === 'ios') {
      return await runRelayQueryRest(teamId, eventType, dateRange, maxDocs);
    }

    try {
      let q = query(
        collection(db, RELAY_COLLECTION),
        where('teamId', '==', teamId),
        where('eventType', '==', eventType),
        limit(maxDocs)
      );

      if (dateRange) {
        q = query(
          collection(db, RELAY_COLLECTION),
          where('teamId', '==', teamId),
          where('eventType', '==', eventType),
          where('date', '>=', dateRange.start),
          where('date', '<=', dateRange.end),
          orderBy('date', 'asc'),
          limit(maxDocs)
        );
      }

      const snapshot = await getDocs(q);
      const relays = snapshot.docs.map((d) => {
        const data: any = d.data();
        return mapRelayDoc({ id: d.id, ...data });
      });
      relays.sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
      return relays;
    } catch (error: any) {
      // Permission/rules evaluation quirks: retry via REST.
      try {
        return await runRelayQueryRest(teamId, eventType, dateRange, maxDocs);
      } catch (restErr) {
        console.error('getTeamRelayResults REST failed:', restErr);
        return [];
      }
    }
  },

  async getUserRelayResults(
    userId: string,
    teamId: string,
    eventType: RelayEventType,
    dateRange?: { start: string; end: string },
    maxDocs: number = 5000
  ): Promise<RelayResult[]> {
    const teamRelays = await this.getTeamRelayResults(teamId, eventType, dateRange, maxDocs);
    return teamRelays.filter((r) => r.legAthleteIds.includes(userId));
  },
};

